import {getFileExtension, type DiffDelta} from '@github-ui/diff-file-tree/diff-file-tree-helpers'
import {ssrSafeDocument} from '@github-ui/ssr-utils'

import {useEffect, useMemo} from 'react'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'

export type FileFilterState = {
  filterText: string
  fileExtensions: Set<string>
  unselectedFileExtensions: Set<string>
  showCodeowners?: boolean
  showDeletedFiles?: boolean
  showGeneratedFiles?: boolean
  showOnlyManifestFiles?: boolean
  showVendorFiles?: boolean
  showViewedFiles?: boolean
}

export function useFileFiltering(
  diffs: Readonly<Array<Readonly<PullRequestFileTreeDiff>>>,
  filters: FileFilterState,
): [DiffDelta[], DiffDelta[]] {
  const diffElementMap = useMemo(() => {
    const diffMap = new Map<string, Element>()

    for (const diff of diffs) {
      const element = ssrSafeDocument?.querySelector(`#diff-${diff.pathDigest}`)

      if (element) {
        diffMap.set(diff.pathDigest, element)
      }
    }

    return diffMap
  }, [diffs])

  const [matchingDiffs, removedDiffs] = useMemo(() => {
    const removedDiffsInt = []
    const matchingDiffsInt = []

    for (const diff of diffs) {
      if (diffMatchesFilter(diff, filters)) {
        matchingDiffsInt.push(diff)
      } else {
        removedDiffsInt.push(diff)
      }
    }

    return [matchingDiffsInt, removedDiffsInt]
  }, [diffs, filters])

  useEffect(() => {
    for (const diff of removedDiffs) {
      const element = diffElementMap.get(diff.pathDigest)

      if (element) {
        element.setAttribute('hidden', 'true')
      } else {
        // Diffs can be loaded in after initial render, so lets check for them again
        const foundElement = ssrSafeDocument?.querySelector(`#diff-${diff.pathDigest}`)

        if (foundElement) {
          foundElement.setAttribute('hidden', 'true')
          diffElementMap.set(diff.pathDigest, foundElement)
        }
      }
    }

    return () => {
      for (const diff of removedDiffs) {
        const element = diffElementMap.get(diff.pathDigest)

        if (element) {
          element.removeAttribute('hidden')
        } else {
          // Diffs can be loaded in after initial render, so lets check for them again
          const foundElement = ssrSafeDocument?.querySelector(`#diff-${diff.pathDigest}`)

          if (foundElement) {
            foundElement.removeAttribute('hidden')
            diffElementMap.set(diff.pathDigest, foundElement)
          }
        }
      }
    }
  }, [diffElementMap, removedDiffs])

  return [matchingDiffs, removedDiffs]
}

function diffMatchesFilter(diff: PullRequestFileTreeDiff, filters: FileFilterState): boolean {
  const match =
    diff.path.toLowerCase().includes(filters.filterText.toLowerCase()) &&
    !filters.unselectedFileExtensions.has(getFileExtension(diff.path)) &&
    (filters.showDeletedFiles || !(diff.changeType === 'REMOVED' || diff.changeType === 'DELETED')) &&
    (!filters.showOnlyManifestFiles || !!diff.isManifestFile) &&
    (!filters.showCodeowners || !!diff.isCodeowner) &&
    (filters.showViewedFiles || !diff.markedAsViewed) &&
    (filters.showGeneratedFiles || !diff.isGenerated) &&
    (filters.showVendorFiles || !diff.isVendored)

  return match
}
